/**
 * Mirrors `src/types/editorial` in envoz-server. Design System §6: types mirror the server, they
 * do not re-invent it — a field the server does not send does not exist here.
 *
 * Dates arrive as ISO strings over JSON even though the server types them as `Date`.
 */

// ── channel ──────────────────────────────────────────────────────────────────

export type EditorialChannel = {
  channelId: string;
  name: string;
  channelUrl: string;
  active: boolean;
  notes: string;
  createdAt: string;
  updatedAt: string;
};

export type EditorialChannelStats = {
  channelId: string;
  streamCount: number;
  /** `endedAt - startedAt` averaged over streams that captured both — the broadcast's actual air
   *  time. Null when no stream on this channel has recorded real timestamps yet. */
  avgDurationMinutes: number | null;
  /** When the channel was last seen live, not when its stream row was last written. */
  lastStreamAt: string | null;
};

export type EditorialChannelWithStats = EditorialChannel & { stats: EditorialChannelStats };

// ── feeds ────────────────────────────────────────────────────────────────────

/** Mirrors `FeedPollStatus` in the server. `unchanged` is a successful read the publisher
 *  answered 304 to — a quiet feed, not a broken one. */
export type FeedPollStatus = 'pending' | 'ok' | 'unchanged' | 'error';

/**
 * A subscribed site. The second way information gets in: a channel is listened to, a source is
 * read — and nothing downstream runs on what it brings, by design.
 */
export type EditorialFeedSource = {
  sourceId: string;
  name: string;
  feedUrl: string;
  siteUrl: string;
  active: boolean;
  notes: string;
  lastStatus: FeedPollStatus;
  lastFetchedAt: string | null;
  /** When this source last brought in something new — not when it was last read. */
  lastItemAt: string | null;
  lastError: string;
  failureCount: number;
  createdAt: string;
  updatedAt: string;
};

export type EditorialFeedSourceWithStats = EditorialFeedSource & { itemCount: number };

/** Things worth telling an editor before they subscribe that are not reasons to refuse. */
export type FeedPreviewWarning = 'empty' | 'no_dates' | 'no_summaries' | 'insecure';

export type FeedPreviewItem = { title: string; link: string; author: string; publishedAt: string | null };

/**
 * What subscribing to an address would actually get, read before anything is written.
 *
 * A diagnosable failure is one of the four non-`ok` outcomes rather than an HTTP error, because
 * "this site refuses non-browser readers, here is the way around it" is an answer the screen
 * renders and acts on.
 */
export type FeedPreview =
  | {
      outcome: 'ok';
      feedUrl: string;
      /** True when the pasted address was a page and this feed is the one it led to. */
      discoveredFromPage: boolean;
      title: string;
      siteUrl: string;
      itemCount: number;
      items: FeedPreviewItem[];
      warnings: FeedPreviewWarning[];
      suggestedSourceId: string;
      suggestedName: string;
      alreadySubscribed: { sourceId: string; name: string; active: boolean } | null;
    }
  | {
      outcome: 'blocked' | 'not_found' | 'not_a_feed' | 'unreachable';
      message: string;
      /** The same publisher through Google News — thinner, and the only way the sites that refuse
       *  us are read at all. */
      googleNewsUrl: string;
    };

/** One post, kept as it arrived. The summary is the feed's own; the article stays at `link`. */
export type EditorialFeedItem = {
  itemId: string;
  sourceId: string;
  sourceName: string;
  title: string;
  link: string;
  author: string;
  summary: string;
  categories: string[];
  publishedAt: string;
  fetchedAt: string;
};

// ── stream ───────────────────────────────────────────────────────────────────

export type EditorialStreamStatus = 'live' | 'reprocessing' | 'completed' | 'error';

export type EditorialStream = {
  streamId: string;
  sourceUrl: string;
  title: string;
  status: EditorialStreamStatus;
  /** Absent on a stream captured before channels were registered. */
  channelId?: string;
  channelName?: string;
  createdAt: string;
  updatedAt: string;
  /** The broadcast's actual air time. Null on a stream captured before this was recorded, or one
   *  that failed before capture finished. */
  startedAt?: string | null;
  endedAt?: string | null;
};

// ── transcript ───────────────────────────────────────────────────────────────

export type EditorialTranscriptTurn = {
  speaker: string;
  text: string;
  start: string;
  end: string;
  start_seconds: number;
  end_seconds: number;
};

export type EditorialTranscriptPayload = {
  source: string;
  stream_id: string;
  transcribed_at: string;
  language: string;
  total_segments: number;
  /** Absent unless asked for: the raw pre-merge segments double the response and the transcript
   *  view reads `turns`. See `includeSegments` on the server's transcript endpoint. */
  segments?: EditorialTranscriptTurn[];
  turns: EditorialTranscriptTurn[];
};

export type EditorialTranscript = {
  streamId: string;
  payload: EditorialTranscriptPayload;
  createdAt: string;
  updatedAt: string;
};

// ── signals + claims ─────────────────────────────────────────────────────────

export type EditorialClaim = {
  claim: string;
  speaker: string;
  timestamp: string;
  speaker_role?: string;
  claim_type?: string;
  modality?: string;
};

export type EditorialClaimRelationship = {
  claim_id: string;
  /** contradicts | supports | elaborates | precedes | follows — the seed of a controversy. */
  relationship: string;
};

/**
 * `changed` reports whether the normalizer edited the model's own first pass — NOT the distance
 * from the transcript. It is routinely `false` on a heavily compressed claim. Composition §5:
 * diff `evidence.excerpt` against `claim_text` instead.
 */
export type EditorialNormalizationMeta = {
  changed: boolean;
  operations: string[];
  confidence: number;
  model: string;
  prompt_version: string;
};

export type EditorialNormalizedClaim = {
  claim_id: string;
  claim_text: string;
  claim_type: string;
  modality: string;
  polarity?: string;
  temporal_scope?: string;
  speaker: { name: string; role: string; confidence: number };
  attribution?: { actor_text: string; source_type: string } | null;
  quantities?: Array<{ value: string; unit: string; context: string }>;
  entities_mentioned?: string[];
  evidence: { excerpt: string; chunk_ids: string[]; timestamps: string[] };
  /** `not_assessed` until step 7 writes a verdict against this claim_id. */
  verification_status?: string;
  normalization?: EditorialNormalizationMeta;
  relationships?: EditorialClaimRelationship[];
  warnings: string[];
};

export type EditorialImportance = 'low' | 'medium' | 'high';
export type EditorialSentimentValue = 'positive' | 'neutral' | 'negative';
export type EditorialStancePosition = 'supportive' | 'neutral' | 'critical';

export type EditorialSignalEntity = { name: string; type: string; confidence: number };
export type EditorialSignalSentiment = { general: EditorialSentimentValue; confidence: number };
export type EditorialSignalStance = { target: string; position: EditorialStancePosition; confidence: number };

export type EditorialSignalPayload = {
  /** Only present on signals saved by the rerun-from-transcript path — the live path doesn't
   *  carry chunk boundaries on the signal itself (they travel alongside it, not inside it). */
  start_seconds?: number;
  end_seconds?: number;
  summary: string;
  topics: string[];
  entities?: EditorialSignalEntity[];
  sentiment?: EditorialSignalSentiment;
  stance?: EditorialSignalStance[];
  /** A closed set the model is prompted with (low|medium|high) — never a bare number. */
  editorial_importance: EditorialImportance;
  why_it_matters?: string;
  possible_news_angle?: string;
  claims: EditorialClaim[];
  normalized_claims: EditorialNormalizedClaim[];
  /** True when both extraction attempts failed and every field above is just the schema default —
   *  without this a dead LLM call and a block where nothing was said drew the same blank card.
   *  Absent on a signal written before this was recorded. */
  extraction_failed?: boolean;
};

export type EditorialSignal = {
  chunkId: string;
  streamId: string;
  payload: EditorialSignalPayload;
  createdAt: string;
  updatedAt: string;
};

// ── criteria matches (step 5) ────────────────────────────────────────────────

export type EditorialCriteriaEvidence = {
  timestamp: string;
  speaker: string;
  text: string;
};

/** The weights the pipeline applied to this match. A layer that did not run carries no weight. */
export type EditorialCriteriaScoreWeights = {
  rules: number;
  semantic: number | null;
  judge: number | null;
};

/** What `finalScore` was compared against to reach `recommendedAction`. */
export type EditorialCriteriaScoreThresholds = {
  matchedAt: number;
  alertAt: number;
  judgeTriggersAt: number;
};

export type EditorialCriteriaMatch = {
  streamId: string;
  chunkId: string;
  criterionId: string;
  /** Empty on matches written before the pipeline carried the criterion's display name. */
  criterionName: string;
  matched: boolean;
  finalScore: number;
  rulesScore: number;
  /** Null when embeddings are disabled. Composition §6: null is a state, zero is a score. */
  semanticScore: number | null;
  /** Null when the preliminary score never crossed JUDGE_TRIGGER_SCORE. */
  judgeScore: number | null;
  reason: string;
  evidence: EditorialCriteriaEvidence[];
  recommendedAction: string;
  /** Null on matches written before these were persisted — the arithmetic panel degrades to a note. */
  weights: EditorialCriteriaScoreWeights | null;
  thresholds: EditorialCriteriaScoreThresholds | null;
  createdAt: string;
};

// ── story candidates (step 5 output) ─────────────────────────────────────────

export type EditorialStoryCandidatePayload = {
  story_id: string;
  stream_id: string;
  title_candidate: string;
  summary: string;
  why_it_matters: string;
  matched_criteria: Array<{ criterion_id: string; score: number }>;
  entities: string[];
  topics: string[];
  urgency: string;
  confidence: number;
  evidence_chunks: string[];
  evidence: EditorialCriteriaEvidence[];
  status: string;
};

export type EditorialStoryCandidate = {
  storyId: string;
  streamId: string;
  payload: EditorialStoryCandidatePayload;
  createdAt: string;
  updatedAt: string;
};

// ── prenota (step 4) ─────────────────────────────────────────────────────────

export type EditorialPrenotaPayload = {
  schema_version: string;
  story_id: string;
  source: {
    stream_id: string;
    source_name: string;
    source_url: string;
    stream_datetime: string;
    language: string;
  };
  headline: string;
  summary: string;
  primary_category: string;
  topics: string[];
  keywords: string[];
  event_type: string;
  entities: Array<{ name: string; type: string; role?: string }>;
  locations: Array<{ name: string; level: string; relationship: string }>;
  claims: Array<{
    claim: string;
    speaker: string;
    timestamp: string;
    claim_type?: string;
    /** Inherited from the normalized claim; a token-overlap match that can miss. */
    claim_id?: string;
  }>;
  /** The reliable story → claims link. Design System §7. */
  source_claim_ids?: string[];
  evidence_chunk_ids: string[];
  generation: { model: string; model_version: string; generated_at: string };
  status: string;
  eligibility?: { decision: string; allowed_formats: string[]; blocking_codes: string[] };
};

export type EditorialPrenota = {
  storyId: string;
  streamId: string;
  payload: EditorialPrenotaPayload;
  createdAt: string;
  updatedAt: string;
};

// ── verification (step 7) ────────────────────────────────────────────────────

export const EDITORIAL_VERDICTS = [
  'supported',
  'partially_supported',
  'contradicted',
  'unresolved',
  'not_verifiable',
  'not_required',
] as const;
export type EditorialVerdict = (typeof EDITORIAL_VERDICTS)[number];

export const PUBLICATION_INSTRUCTIONS = [
  'state_as_fact_with_citation',
  'state_with_reduced_scope',
  'attribute_to_speaker',
  'report_as_dispute',
  'do_not_publish',
] as const;
export type EditorialPublicationInstruction = (typeof PUBLICATION_INSTRUCTIONS)[number];

export type EditorialVerificationStatus =
  | 'verified'
  | 'contradiction_lead'
  | 'blocked_critical_claim'
  | 'blocked_no_sources'
  | 'blocked_input'
  | 'research_failed';

export type EditorialClaimAssessment = {
  claim_id: string;
  materiality: string;
  verifiability: string;
  risk: string;
  volatility: string;
  verdict: EditorialVerdict;
  confidence: number;
  supported_scope: string;
  unsupported_scope: string | null;
  evidence_ids: string[];
  contradicting_evidence_ids: string[];
  provenance_group_count: number;
  publication_instruction: EditorialPublicationInstruction;
  verified_at: string;
  expires_at: string | null;
  notes: string[];
};

export type EditorialProposedCorrection = {
  correction_id: string;
  field: string;
  before: string;
  after: string;
  reason: string;
  confidence: number;
  evidence_ids: string[];
  status: 'proposed' | 'accepted' | 'rejected';
};

/**
 * What an external claim *is*, which decides the verbs a sentence resting on it may use and
 * whether it has to be attributed. Absent on facts written before the pipeline classified them —
 * the panel falls back to the strictest reading rather than to the loosest.
 */
export const EPISTEMIC_STATUSES = [
  'verified_fact',
  'official_statement',
  'original_reporting',
  'attributed_claim',
  'attributed_unresolved',
  'editorial_inference',
  'contradicted',
  'insufficient_evidence',
] as const;
export type EditorialEpistemicStatus = (typeof EPISTEMIC_STATUSES)[number];

export type EditorialRiskLevel = 'low' | 'medium' | 'high' | 'critical';

export type EditorialExternalFact = {
  external_fact_id: string;
  /** Precise for audit, and routinely not the sentence a note would print. */
  statement: string;
  fact_type: string;
  evidence_ids: string[];
  source_ids: string[];
  confidence: number;
  publication_instruction: EditorialPublicationInstruction;
  /** Everything below arrives from the epistemic-discipline phase; older facts carry none of it. */
  epistemic_status?: EditorialEpistemicStatus;
  event_date?: string | null;
  /** Independent origins, not urls. Ten portals reprinting one bulletin are one entry. */
  source_family_ids?: string[];
  attribution_required?: boolean;
  minimum_public_qualifier?: string | null;
  /** The publishable form, already stripped of the audit language in `statement`. */
  public_claim_core?: string;
  forbidden_upgrades?: string[];
  required_counterweights?: string[];
  risk_level?: EditorialRiskLevel;
  human_review_required?: boolean;
  stale?: boolean;
};

/** What was reviewed, which is the only thing that licenses an absence in the prose. */
export type EditorialCoverageFinding = {
  coverage_finding_id: string;
  scope: string;
  transcript_segment_ids: string[];
  finding: string;
  confidence: 'low' | 'medium' | 'high';
  allowed_public_expression: string;
  forbidden_expression?: string;
};

export type EditorialConflict = {
  conflict_id: string;
  claim_id: string | null;
  description: string;
  evidence_ids: string[];
  contradicting_evidence_ids: string[];
  must_appear_in_note: boolean;
};

export type EditorialSource = {
  source_id: string;
  source_class: string;
  publisher: string;
  title: string;
  canonical_url: string;
  author: string | null;
  published_at: string | null;
  retrieved_at: string;
  language: string;
  provenance_group: string;
  registry_status: string;
};

export type EditorialEvidence = {
  evidence_id: string;
  source_id: string;
  evidence_type: string;
  excerpt: string;
  supports: string[];
  captured_at: string;
};

export type EditorialResearchTrace = {
  question_id: string;
  question: string;
  provider: string;
  model: string;
  queries_run: string[];
  candidates_returned: number;
  candidates_rejected: Array<{ url: string; reason: string; detail: string }>;
  documents_fetched: number;
};

export type EditorialVerificationPayload = {
  schema_version: string;
  verification_id: string;
  story_key: string;
  story_id: string;
  status: EditorialVerificationStatus;
  as_of: string;
  claim_assessments: EditorialClaimAssessment[];
  proposed_corrections: EditorialProposedCorrection[];
  entity_resolutions: EditorialProposedCorrection[];
  external_facts: EditorialExternalFact[];
  coverage_findings?: EditorialCoverageFinding[];
  conflicts: EditorialConflict[];
  sources: EditorialSource[];
  evidence: EditorialEvidence[];
  research_trace: EditorialResearchTrace[];
  warnings: string[];
};

export type EditorialVerification = {
  storyKey: string;
  storyId: string;
  streamId: string;
  idempotencyKey: string;
  status: string;
  payload: EditorialVerificationPayload;
  createdAt: string;
  updatedAt: string;
};

// ── enrichment (step 7, profile-scoped) ──────────────────────────────────────

export type EditorialEnrichmentPayload = {
  schema_version: string;
  enrichment_id: string;
  story_key: string;
  story_id: string;
  verification_id: string;
  status: string;
  context_units: Array<{
    context_unit_id: string;
    context_type: string;
    purpose: string;
    narrative_role: string;
    relevance_score: number;
    required: boolean;
    fact_refs: string[];
    source_ids: string[];
  }>;
  narrative_brief: {
    central_question: string;
    angle_status: string;
    target_words: { min: number; max: number; hard_min: number };
    headline_guardrails: string[];
    required_caveats: string[];
    prohibited_inferences: string[];
    /** Verbatim formulations a gate matches — phrases, not descriptions of them. */
    forbidden_claims?: string[];
    risks?: string[];
    human_review_required?: boolean;
  };
  warnings: string[];
};

export type EditorialEnrichment = {
  enrichmentId: string;
  storyKey: string;
  storyId: string;
  streamId: string;
  profileId: string;
  payload: EditorialEnrichmentPayload;
  createdAt: string;
  updatedAt: string;
};

// ── X social review (step 5b) ────────────────────────────────────────────────

/** Mirrors `SocialClassification` in envoz-server's x_social_review schema. */
export type SocialClassification = 'irrelevant' | 'emerging' | 'relevant' | 'high' | 'breaking_candidate';

/** The six graded dimensions. Closed set, so `satisfies Record<XDimension, …>` in `relevance.ts`
 *  fails to compile if the pipeline adds a seventh and nobody writes its rubric entry. */
export type XDimension =
  | 'story_match'
  | 'conversation_breadth'
  | 'participant_authority'
  | 'narrative_richness'
  | 'momentum'
  | 'observable_engagement';

/** Mirrors `PenaltyDetected.type` in the pipeline's `x_social_review.py`. */
export type XPenaltyType =
  | 'event_mismatch'
  | 'homonym_or_topic_collision'
  | 'single_account_dominance'
  | 'repeated_account_content'
  | 'suspected_coordination'
  | 'stale_conversation'
  | 'insufficient_evidence';

export type XConfidence = 'high' | 'medium' | 'low';

/** Grades run 0–4, never 0–100. Rendering one on a 0–100 track is what made every dimension
 *  read as empty before. */
export type EditorialXDimensionGrade = {
  grade: number;
  reason: string;
  supporting_post_ids: string[];
};

/** The pipeline writes `reason` and `supporting_post_ids`; there is no `severity`/`evidence`. */
export type EditorialXPenalty = {
  type: XPenaltyType;
  reason: string;
  supporting_post_ids?: string[];
};

/** What the run cost and which prompt produced it — the only way two runs of the same story
 *  can be compared (pipeline schema §25). */
export type EditorialXTechnicalUsage = {
  model: string;
  prompt_version?: string;
  x_search_calls?: number;
  input_tokens?: number;
  reasoning_tokens?: number;
  output_tokens?: number;
  duration_ms?: number;
  queries_proposed?: number;
  posts_selected?: number;
  unique_accounts?: number;
};

export type EditorialXReviewPayload = {
  schema_version: string;
  story_id: string;
  headline?: string;
  source_story_ids?: string[];
  /** Only present on skipped/failed reviews — a scored review has `overall_score` instead. */
  social_review_status?: 'skipped' | 'failed';
  skip_reason?: string;
  failure_type?: string;
  error?: string;
  retryable?: boolean;
  base_score?: number;
  penalty_score?: number;
  overall_score?: number;
  classification?: SocialClassification;
  confidence?: XConfidence;
  /** Rules that overrode the score band, e.g. `story_match<=1→irrelevant`. Empty when none fired. */
  gates_applied?: string[];
  penalties_detected?: EditorialXPenalty[];
  dimensions?: Partial<Record<XDimension, EditorialXDimensionGrade>>;
  analysis_window?: { from_date: string; to_date: string };
  technical_usage?: EditorialXTechnicalUsage;
  dominant_narratives?: Array<{
    narrative: string;
    prevalence: string;
    representative_stance: string;
    supporting_post_ids: string[];
  }>;
  representative_posts?: Array<{
    post_id: string;
    url: string;
    account: string;
    account_handle: string;
    account_type: string;
    /** Grok cannot always read a timestamp off a post — the pipeline persists null when it can't. */
    published_at?: string | null;
    text_excerpt: string;
    stance_target: string;
    stance: string;
    why_relevant: string;
    visible_metrics?: {
      likes?: number;
      reposts?: number;
      replies?: number;
      views?: number;
    };
  }>;
  limitations?: string[];
  generated_at?: string;
};

export type EditorialXReview = {
  storyId: string;
  streamId: string;
  payload: EditorialXReviewPayload;
  createdAt: string;
  updatedAt: string;
};

// ── editorial output / note (step 8) ─────────────────────────────────────────

export type EditorialOutputCitation = {
  span: { start: number; end: number };
  anchor_text: string;
  external_fact_id: string;
  source_ids: string[];
  display: string;
};

export type EditorialOutputBodyBlock = {
  type: string;
  text: string;
  /**
   * Absent — not empty — on blocks the drafter wrote without citing a claim (transitions, framing).
   * Treat a missing key and an empty array the same way: the block is unbacked, and Composition §8
   * requires that to be visible rather than invisible.
   */
  claim_ids?: string[];
  /** Same absent-vs-empty rule as `claim_ids`, for facts pulled in from outside the stream. */
  fact_ids?: string[];
  support_refs?: Array<{ type: string; id: string }>;
  /** Computed anchors into `text` — never written by the model (transformation manual §13.1). */
  citations?: EditorialOutputCitation[];
};

export type EditorialOutputContent = {
  format: string;
  headline: string;
  deck: string;
  body_blocks: EditorialOutputBodyBlock[];
};

export type EditorialFactualFinding = {
  block_index?: number;
  claim_ids_cited?: string[];
  fact_ids_cited?: string[];
  classification: string;
  detail?: string;
};

export type EditorialDeviation = {
  field: string;
  severity: string;
  expected?: string;
  actual?: string;
};

export type EditorialBlockingNote = {
  problem?: string;
  classification?: string;
  field?: string;
  detail?: string;
};

/** The profile's own declared configuration, resolved at generation time. */
export type EditorialResolvedConfiguration = {
  orientation?: { label?: string };
  treatment?: string;
  expressive_intensity?: string;
};

export type EditorialOutputPayload = {
  schema_version: string;
  /**
   * The story was drafted without a relevance score because X could not be reached — not because
   * it scored well. Absent on every draft that went through the gate normally.
   */
  relevance_unverified?: boolean;
  transformation_id: string;
  story_id: string;
  status: string;
  generated_at: string;
  profile_reference: { profile_id: string; version: number };
  resolved_editorial_configuration?: EditorialResolvedConfiguration;
  content: EditorialOutputContent | null;
  traceability?: {
    used_claim_ids?: string[];
    used_external_fact_ids?: string[];
    used_context_unit_ids?: string[];
    published_source_ids?: string[];
    omitted_claims?: Array<{ claim_id: string; reason: string }>;
    factual_spans?: Array<{ json_pointer: string; claim_ids: string[] }>;
  };
  validation?: {
    factual?: { decision?: string; findings?: EditorialFactualFinding[] };
    editorial?: { decision?: string; checks?: unknown[]; deviations?: EditorialDeviation[] };
    /** Orphan links must be zero (transformation manual §20) — a fact with no anchor is a bug. */
    citations?: { decision?: string; orphan_links?: number; codes?: string[] };
    /** Present from editorial-revision-v1 on; older outputs only carry the raw findings. */
    blocking_notes?: EditorialBlockingNote[];
    revision?: { attempts?: number; result?: string; notes_addressed?: number };
    /** Which §8.1 gate stopped the piece, counted by code. Absent before the gates existed. */
    evidence_discipline?: { decision?: string; codes?: Record<string, number> };
    /** The separate legibility pass: applied, discarded (and why), not needed, or never run. */
    readability?: { decision?: string; reason?: string; changes?: string[]; defects_addressed?: number };
    /** Whether this piece may ever publish unattended, from the facts the text actually cites. */
    risk?: { decision?: string; level?: EditorialRiskLevel; reasons?: string[]; human_review_required?: boolean };
    length?: { decision?: string; word_count?: number; preferred_min?: number; preferred_max?: number };
  };
  generation?: {
    model: string;
    /** Which account wrote this note: `chatgpt`, `kimi` or `local`. Absent on older drafts. */
    provider?: string;
    /** False when the box's own model wrote it, so the note reads differently from a paid one. */
    used_external_model?: boolean;
    /** Why the box wrote it, when it did. Empty when a paid model was available. */
    provider_note?: string;
    prompt_versions: Record<string, string>;
    input_hash: string;
    duration_ms: number;
    /** Milliseconds by stage. Empty on drafts written before it was recorded. */
    stage_ms?: Record<string, number>;
  };
  /** True when this row is a stored answer replayed for an identical request, not a fresh run. */
  reused_execution?: boolean;
  /** The verification is stored once per story_key and shared by every profile — a reference,
   *  not a copy (transformation manual §13.1). */
  verification_reference?: { verification_id: string; story_key: string; status: string; as_of: string };
  enrichment_reference?: { enrichment_id: string; status: string };
};

export type EditorialOutput = {
  transformationId: string;
  storyId: string;
  streamId: string;
  profileId: string;
  outputFormat: string;
  payload: EditorialOutputPayload;
  createdAt: string;
  updatedAt: string;
};

// ── human review (step 9) ────────────────────────────────────────────────────

export type EditorialReviewAction = 'approved' | 'edited' | 'rejected' | 'published';

export type EditorialReview = {
  review_id: string;
  transformation_id: string;
  story_id: string;
  action: EditorialReviewAction;
  editor_id: string;
  reason_category: string;
  comment: string;
  severity: string;
  responsible_stage: string;
  recommendation: string;
  destination: string;
  diff: string;
  metrics: {
    edit_distance_words: number;
    changed_blocks: number;
    blocks_before: number;
    blocks_after: number | null;
    external_material_added: boolean;
  };
  reviewed_at: string;
};

// ── topics ───────────────────────────────────────────────────────────────────

export type EditorialTopicsPayload = {
  source: string;
  stream_id: string;
  analyzed_at: string;
  topics: string[];
  entities: Array<{ name: string; type: string; mentions: number }>;
  key_claims: Array<{ claim: string; speaker: string; timestamp: string }>;
  chunks: Array<{ start: string; end: string; topics: string[]; summary: string; entities: string[] }>;
};

export type EditorialTopics = {
  streamId: string;
  payload: EditorialTopicsPayload;
  createdAt: string;
  updatedAt: string;
};

// ── voice profiles (speaker identification) ──────────────────────────────────

/**
 * A voice the system can recognise. `pending_label` is one the pipeline discovered on its own and
 * named `Voz 3`: it already matches segments — that is how it collects clips — but nobody has said
 * who it is yet. Turning those into names is the profile panel's main job.
 */
export type EditorialSpeakerProfileStatus = 'active' | 'pending_label' | 'archived';

export type EditorialSpeakerProfile = {
  profileId: string;
  name: string;
  slug: string;
  status: EditorialSpeakerProfileStatus;
  isAuto: boolean;
  /** Included clips only — what the voice fingerprint was actually built from. */
  clipCount: number;
  excludedClipCount: number;
  /** False when every clip has been excluded: the profile survives but stops matching. */
  hasCentroid: boolean;
  /** Segments claimed across all streams. Zero on an old profile means duplicate or gone quiet. */
  matchCount: number;
  lastMatchedAt: string | null;
  notes: string;
  createdAt: string;
  updatedAt: string;
};

export type EditorialSpeakerClip = {
  clipId: string;
  profileId: string;
  /** False when the audio never uploaded — the clip still trains the profile, it just cannot be
   *  played back. Composition §6: that is a state to show, not a row to hide. */
  hasAudio: boolean;
  streamId: string;
  chunkId: string;
  startSeconds: number;
  endSeconds: number;
  durationSeconds: number;
  transcriptText: string;
  source: string;
  /** How confident the match was when this clip joined. The low ones are the first suspects when
   *  a profile starts claiming the wrong voice. */
  similarityAtCapture: number | null;
  included: boolean;
  createdAt: string;
};

/**
 * Mirrors the server's accepted set. Deletion is absent on purpose: the editorial API has no
 * authentication, so it only accepts edits that can be undone. Permanently removing a voice and
 * its recordings is done from a shell on the pipeline box.
 */
export const SPEAKER_COMMAND_TYPES = [
  'rename_profile',
  'set_clip_included',
  'recompute_centroid',
  'archive_profile',
  'merge_profiles',
] as const;

export type EditorialSpeakerCommandType = (typeof SPEAKER_COMMAND_TYPES)[number];

/**
 * Issued by their own endpoints rather than by `submit-command`: creating a voice mints an id,
 * and uploading audio has to put a file in the bucket first. They still appear in the queue —
 * an editor has to see the clip being imported, not a gap where it was.
 */
export const SPEAKER_COMMAND_TYPES_INTERNAL = ['create_profile', 'add_uploaded_clip'] as const;

export type EditorialSpeakerCommandTypeInternal = (typeof SPEAKER_COMMAND_TYPES_INTERNAL)[number];

export type EditorialSpeakerCommandStatus = 'pending' | 'running' | 'done' | 'failed';

/** An edit waiting for the pipeline box to apply it — the only machine with the training audio. */
export type EditorialSpeakerCommand = {
  commandId: string;
  type: EditorialSpeakerCommandType | EditorialSpeakerCommandTypeInternal;
  profileId: string;
  payload: Record<string, unknown>;
  status: EditorialSpeakerCommandStatus;
  requestedBy: string;
  requestedAt: string;
  finishedAt: string | null;
  error: string;
};

// ── processing queue ─────────────────────────────────────────────────────────

/**
 * The pipeline in dependency order. A stage runs only once every stage before it has produced its
 * artifact, which is why the picker below offers a point to stop at rather than a free selection.
 */
export const PIPELINE_STAGES = ['transcript', 'topics', 'claims', 'prenota', 'x_review', 'topic_link', 'notas'] as const;

export type PipelineStage = (typeof PIPELINE_STAGES)[number];

export type PipelineJobStatus = 'pending' | 'running' | 'done' | 'failed' | 'cancelled';

/**
 * A processing request. The box that runs the pipeline sleeps most of the day, so this is a row
 * waiting to be claimed rather than a call — `currentStage` is the only progress there is while
 * a three-hour broadcast is being transcribed.
 */
export type PipelineJob = {
  jobId: string;
  sourceUrl: string;
  streamId: string;
  stages: PipelineStage[];
  status: PipelineJobStatus;
  currentStage: string;
  stagesDone: string[];
  requestedBy: string;
  error: string;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  /** When set and in the future, the job is deliberately waiting — for a model to answer, or
   *  between capture retries. Distinct from "queued behind a busy machine". */
  notBefore: string | null;
  /** Set as soon as the run has a stream, so a job can be followed to its broadcast mid-run. */
  resolvedStreamId: string;
};

/**
 * How far a broadcast got, as numbers rather than as artifacts.
 *
 * The orientation screens — resumen, la barra de etapas, el flujo — draw counts. They used to get
 * them by downloading nine collections in full, about a megabyte per broadcast, to call `.length`
 * on each. The counts are now made in the database.
 */
/** Work a broadcast still has queued, and the reason it has not happened. Art Direction §7:
 *  absence is a state, not a blank — "no model answered" and "ran and found nothing" are
 *  different facts and must not be drawn the same way. */
export type PendingWork = { stages: string[]; status: string; reason: string };

export type EditorialStreamSummary = {
  streamId: string;
  turns: number;
  signals: number;
  claims: number;
  /** Evaluations that matched — what the Relevancia tab lists. */
  criteriaMatches: number;
  /** Every evaluation run, matched or not. Absent on an older API. */
  criteriaEvaluated?: number;
  storyCandidates: number;
  prenotas: number;
  xReviews: number;
  relevanceWatches: number;
  alertEvents: number;
  /** Stories linked to a canonical topic. Absent on an older API. */
  topicMentions?: number;
  verifications: number;
  /** verdict → how many claim assessments carry it. Absent verdicts are simply not keys. */
  verdicts: Partial<Record<EditorialVerdict, number>>;
  outputs: number;
  profiles: number;
  reviews: number;
  pendingWork: PendingWork | null;
};

// ── people ───────────────────────────────────────────────────────────────────

export const USER_ROLES = ['admin', 'editor'] as const;
export type UserRole = (typeof USER_ROLES)[number];

export const USER_STATUSES = ['active', 'inactive'] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

export type Person = {
  userId: string;
  name: string;
  email: string;
  role: UserRole;
  status: UserStatus;
  createdAt: string;
  modifiedAt: string;
};

/** What the transcripts called a voice, and how many of its own clips back that name. Offered as
 *  a suggestion in the voice panel — the pipeline never applies it on its own. */
export type SuggestedName = { name: string; clips: number };


// ── canonical topics ─────────────────────────────────────────────────────────

/**
 * A subject that outlives the broadcast that found it. Every other record here is scoped to one
 * stream or one calendar day; this one references many stories across months.
 */
export type EditorialTopic = {
  topicId: string;
  canonicalName: string;
  aliases: string[];
  category: string;
  entities: { name: string; normalizedName: string; type: string }[];
  parentTopicId: string | null;
  relatedTopicIds: string[];
  status: 'active' | 'merged' | 'archived';
  mergedIntoTopicId: string | null;
  firstSeenAt: string | null;
  lastSeenAt: string | null;
  mentionCount: number;
};

export type EditorialTopicTrend = {
  topicId: string;
  /** Sparse: days with no mention are absent, not zero. */
  timeline: { day: string; mentions: number }[];
  momentum: number;
  heat: number;
  daysSincePrevious: number | null;
  resurfaced: boolean;
  byChannel: { channelId: string; channelName: string; mentions: number }[];
  peak: { day: string; mentions: number } | null;
};

export type EditorialTopicMention = {
  topicId: string;
  storyId: string;
  storyKey: string;
  streamId: string;
  channelId: string;
  channelName: string;
  headline: string;
  mentionedAt: string | null;
  matchType: string;
  matchConfidence: number;
  matchMethod: string;
  xRelevanceScore: number | null;
};

export type EditorialTopicWithTrend = EditorialTopic & {
  trend: Pick<EditorialTopicTrend, 'heat' | 'momentum' | 'resurfaced'>;
};

// ── relevance watches ────────────────────────────────────────────────────────

/** Why a watch stopped. `watching` is one of the answers — it may still be open. */
export type EditorialWatchStatus = 'watching' | 'collapsed' | 'faded' | 'completed' | 'expired';

export type EditorialRelevanceSample = {
  observedAt: string | null;
  score: number;
  classification: string | null;
  confidence: string | null;
  /** `x_review` is the first measurement; `resample` a paid re-read taken hours later. */
  source: string;
  postsSeen: number;
};

/**
 * How one story's X relevance moved after the first measurement.
 *
 * The score on `EditorialXReview` is a single reading taken minutes after the broadcast said it.
 * This is the readings that came after — opened at a low floor rather than at the 65 the drafter
 * acts on, because the story worth following is the one that was not actionable yet.
 */
export type EditorialRelevanceWatch = {
  watchId: string;
  storyId: string;
  sourceStoryIds: string[];
  streamId: string;
  topicId: string;
  headline: string;
  openedAt: string | null;
  expiresAt: string | null;
  nextSampleAt: string | null;
  status: EditorialWatchStatus;
  closedAt: string | null;
  closedReason: string;
  /** Oldest first — this is the curve the screens draw. */
  samples: EditorialRelevanceSample[];
  resampleCount: number;
  initialScore: number;
  peakScore: number;
  peakAt: string | null;
  latestScore: number;
  latestAt: string | null;
  crossedActionThreshold: boolean;
};

// ── alerts ───────────────────────────────────────────────────────────────────

/**
 * Where a rule may match, in the order the pipeline reaches them: raw speech seconds after it was
 * said, the same chunk after extraction, the extracted story minutes later, and — not text at all
 * — a watched story's relevance crossing the threshold.
 */
export type EditorialAlertSurface = 'chunk_text' | 'chunk_signal' | 'prenota' | 'relevance' | 'feed_item';

export type EditorialAlertNotificationStatus = 'not_required' | 'pending' | 'sent' | 'failed';

export type EditorialAlertRule = {
  ruleId: string;
  name: string;
  keywords: string[];
  /** True requires every keyword in the same text; false fires on any one of them. */
  matchAll: boolean;
  surfaces: EditorialAlertSurface[];
  /** Empty means every channel. */
  channelIds: string[];
  minRelevanceScore: number | null;
  notifyEmails: string[];
  cooldownMinutes: number;
  active: boolean;
  createdBy: string;
  createdAt: string | null;
  updatedAt: string | null;
};

export type EditorialAlertEvent = {
  eventId: string;
  ruleId: string;
  /** Denormalized by the pipeline, so the trail still reads after its rule is deleted. */
  ruleName: string;
  surface: EditorialAlertSurface;
  matchedKeywords: string[];
  excerpt: string;
  streamId: string;
  channelId: string;
  channelName: string;
  chunkId: string;
  /** Offset into the broadcast — what orders the timeline. `occurredAt` is when it was noticed. */
  spokenSeconds: number | null;
  storyId: string;
  topicId: string;
  relevanceScore: number | null;
  previousScore: number | null;
  crossing: 'crossed_action_threshold' | 'collapsed' | 'faded' | null;
  /** `feed_item` only: the copied post that matched, its subscription, and the publisher's page —
   *  which is what this alert opens, since there is no broadcast behind it. */
  feedItemId: string;
  feedSourceId: string;
  feedLink: string;
  occurredAt: string | null;
  notificationStatus: EditorialAlertNotificationStatus;
  notifyEmails: string[];
  notifiedAt: string | null;
  notificationError: string;
};

/**
 * What the machine cost to run, as the server aggregates it.
 *
 * Money arrives as a string and stays one. These are fractions of a cent per call summed over
 * thousands of calls; the screen prints what the server computed and never does arithmetic on it.
 */
export type EditorialLlmCostTotals = {
  calls: number;
  inputTokens: number;
  cachedInputTokens: number;
  outputTokens: number;
  costUsd: string;
  /** Calls on a model the box has no published price for. Counted apart, never read as free. */
  unpricedCalls: number;
};

/** One model, one prompt version or one broadcast, depending on the grouping. */
export type EditorialLlmCostBucket = EditorialLlmCostTotals & { key: string };

export type EditorialLlmCostDay = { day: string; costUsd: string; calls: number };

export type EditorialLlmCosts = {
  windowDays: number;
  totals: EditorialLlmCostTotals;
  byDay: EditorialLlmCostDay[];
  byModel: EditorialLlmCostBucket[];
  byStage: EditorialLlmCostBucket[];
  byStream: EditorialLlmCostBucket[];
};
